<script setup>
import { computed, ref, watch, useId } from 'vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import {
	DEFAULT_PMT_PID,
	PID_MAX,
	PID_MIN,
	buildPidsSpec,
	buildPushTarget,
	nextTrackKey,
	parsePid,
	parsePidsSpec,
	supportsPidMapping,
	trackLabel,
	validatePidMapping,
} from '@/Utils/srtPushTarget'

const props = defineProps({
	// destination protocol; must be listed in PID_MAPPING_PROTOCOLS
	protocol: { type: String, default: 'srt', validator: supportsPidMapping },
	// parent stream's ingest: 'ts', 'other' or null when unknown (see streamIngestKind)
	ingestKind: { type: String, default: null },
	pids: { type: String, default: '' },
	pmtPid: { type: [String, Number], default: '' },
	// push target preview inputs
	host: { type: String, default: '' },
	port: { type: [String, Number], default: '' },
	streamId: { type: String, default: '' },
	latency: { type: [String, Number], default: '' },
	passphrase: { type: String, default: '' },
})

const emit = defineEmits({
	'update:pids': () => true,
	'update:pmtPid': () => true,
	'update:valid': () => true,
})

const uid = useId()
const MODES = [
	{ value: 'standard', label: 'Standard' },
	{ value: 'source', label: 'Keep source PIDs' },
	{ value: 'custom', label: 'Custom' },
]

const mode = ref('standard')
const rows = ref([])
const scte35 = ref('')
const extra = ref([])
const pmt = ref('')
const advancedOpen = ref(false)
const addTrackOpen = ref(false)
let lastEmittedPids = null

function load(spec) {
	const state = parsePidsSpec(spec)
	mode.value = state.mode
	rows.value = state.rows
	scte35.value = state.scte35
	extra.value = state.extra
	addTrackOpen.value = false
}

watch(() => props.pids, (value) => {
	if ((value || '') !== lastEmittedPids) load(value)
}, { immediate: true })

watch(() => props.pmtPid, (value) => {
	const text = value === null || value === undefined ? '' : String(value)
	if (text !== pmt.value) pmt.value = text
	if (text) advancedOpen.value = true
}, { immediate: true })

const spec = computed(() => buildPidsSpec({ mode: mode.value, scte35: scte35.value, rows: rows.value, extra: extra.value }))
const pmtValue = computed(() => parsePid(pmt.value).text || pmt.value.trim())
const validation = computed(() => validatePidMapping({ mode: mode.value, scte35: scte35.value, rows: rows.value, pmtPid: pmt.value }))

watch(spec, (value) => {
	lastEmittedPids = value
	emit('update:pids', value)
}, { immediate: true })
watch(pmtValue, (value) => emit('update:pmtPid', value), { immediate: true })
watch(() => validation.value.valid, (valid) => emit('update:valid', valid), { immediate: true })

const preview = computed(() => buildPushTarget({
	host: props.host,
	port: props.port,
	streamId: props.streamId,
	latency: props.latency,
	passphrase: props.passphrase,
	pids: spec.value,
	pmtPid: pmtValue.value,
}, { protocol: props.protocol }))

const sourceUnavailable = computed(() => props.ingestKind === 'other')
const SOURCE_UNAVAILABLE = 'Only applies to streams ingested over SRT'

const hasScte35Row = computed(() => rows.value.some((row) => row.key === 'scte35'))
const nextVideo = computed(() => nextTrackKey(rows.value, 'v'))
const nextAudio = computed(() => nextTrackKey(rows.value, 'a'))

function setMode(value) {
	mode.value = value
}

function addRow(key) {
	if (!key) return
	const scteIndex = rows.value.findIndex((row) => row.key === 'scte35')
	const row = { key, pid: '' }
	if (scteIndex >= 0 && key !== 'scte35') rows.value.splice(scteIndex, 0, row)
	else rows.value.push(row)
	addTrackOpen.value = false
}

function removeRow(index) {
	rows.value.splice(index, 1)
}

const isBaseRow = (row) => row.key === 'v' || row.key === 'a'
const fieldId = (key) => `srt-pid-${uid}-${key}`
const errorFor = (field) => validation.value.errors[field]
</script>

<template>
	<section class="srt-pid-mapping mb-3" data-testid="srt-pid-mapping">
		<p class="text-tiny font-medium text-surface-text-1 mb-2">PID mapping</p>
		<TabGroup type="segmented" :cols="3" class="mb-2">
			<template v-for="item in MODES" :key="item.value">
				<Tooltip v-if="item.value === 'source'" wrapper-class="block w-full" wrap-trigger
					:active="sourceUnavailable" :content="SOURCE_UNAVAILABLE">
					<TabItem class="w-full" :is-active="mode === item.value" :disabled="sourceUnavailable"
						:aria-disabled="sourceUnavailable || undefined" @click="setMode(item.value)">
						{{ item.label }}
					</TabItem>
				</Tooltip>
				<TabItem v-else :is-active="mode === item.value" @click="setMode(item.value)">
					{{ item.label }}
				</TabItem>
			</template>
		</TabGroup>

		<p v-if="mode === 'standard'" class="text-xs text-surface-7 mb-0">
			Castr defaults: video 257, audio 255–256
		</p>

		<div v-else-if="mode === 'source'">
			<p class="text-xs text-surface-7 mb-1">
				Tracks keep the PIDs they arrive with; tracks without one use the default.
			</p>
			<p v-if="ingestKind === null" class="text-xs text-surface-7 mb-2" data-testid="srt-pid-source-help">
				Applies to streams ingested over SRT; other ingests use the default PIDs.
			</p>
			<p v-else-if="sourceUnavailable" class="text-xs text-warning mb-2">
				{{ SOURCE_UNAVAILABLE }}: this stream uses the default PIDs.
			</p>
			<label class="block text-tiny text-surface-8 mb-1" :for="fieldId('scte35-pin')">
				Pin SCTE-35 PID <span class="text-surface-6">(optional)</span>
			</label>
			<FormInput :id="fieldId('scte35-pin')" v-model="scte35" size="sm" placeholder="e.g. 0x1F0"
				:is-error="!!errorFor('scte35')" />
			<p v-if="errorFor('scte35')" class="text-xs text-error mt-1 mb-0">{{ errorFor('scte35') }}</p>
			<p v-else-if="scte35.trim()" class="text-xs text-warning mt-1 mb-0">
				If a source track already uses this PID, that track falls back to the default.
			</p>
		</div>

		<div v-else>
			<div v-for="(row, index) in rows" :key="row.key" class="flex items-start gap-2 mb-2">
				<label class="w-24 shrink-0 text-tiny text-surface-8 pt-1.5" :for="fieldId(row.key)">{{ trackLabel(row.key) }}</label>
				<div class="grow min-w-0">
					<FormInput :id="fieldId(row.key)" v-model="row.pid" size="sm" placeholder="e.g. 211 or 0xD3"
						:is-error="!!errorFor(`rows.${index}`)" />
					<p v-if="errorFor(`rows.${index}`)" class="text-xs text-error mt-1 mb-0">{{ errorFor(`rows.${index}`) }}</p>
				</div>
				<FormButton v-if="!isBaseRow(row)" type="link" size="sm" class="shrink-0 !px-2"
					:aria-label="`Remove ${trackLabel(row.key)}`" :title="`Remove ${trackLabel(row.key)}`" @click="removeRow(index)">
					&times;
				</FormButton>
			</div>
			<p v-if="validation.errors.rows" class="text-xs text-error mb-2">{{ validation.errors.rows }}</p>
			<div class="flex flex-wrap items-center gap-2">
				<FormButton type="secondary" size="sm" @click="addTrackOpen = !addTrackOpen">+ Add track</FormButton>
				<template v-if="addTrackOpen">
					<FormButton type="outline" size="sm" :disabled="!nextVideo" @click="addRow(nextVideo)">
						{{ nextVideo ? trackLabel(nextVideo).replace(' PID', '') : 'Video' }}
					</FormButton>
					<FormButton type="outline" size="sm" :disabled="!nextAudio" @click="addRow(nextAudio)">
						{{ nextAudio ? trackLabel(nextAudio).replace(' PID', '') : 'Audio' }}
					</FormButton>
				</template>
				<FormButton v-if="!hasScte35Row" type="secondary" size="sm" @click="addRow('scte35')">+ SCTE-35</FormButton>
			</div>
			<p class="text-xs text-surface-7 mt-2 mb-0">Decimal or hex, {{ PID_MIN }}–{{ PID_MAX }}. Empty rows keep the default PID.</p>
		</div>

		<div class="mt-3">
			<button type="button" class="text-tiny text-surface-8 hover:text-white inline-flex items-center gap-1"
				:aria-expanded="advancedOpen" @click="advancedOpen = !advancedOpen">
				<span class="inline-block transition-transform" :class="advancedOpen ? 'rotate-90' : ''">›</span>
				Advanced
			</button>
			<div v-if="advancedOpen" class="mt-2">
				<label class="block text-tiny text-surface-8 mb-1" :for="fieldId('pmt')">PMT PID</label>
				<FormInput :id="fieldId('pmt')" v-model="pmt" size="sm" :placeholder="String(DEFAULT_PMT_PID)"
					:is-error="!!errorFor('pmtPid')" />
				<p v-if="errorFor('pmtPid')" class="text-xs text-error mt-1 mb-0">{{ errorFor('pmtPid') }}</p>
			</div>
		</div>

		<div class="mt-3 rounded bg-surface-1 ring-1 ring-surface-3 px-3 py-2">
			<p class="text-xs text-surface-7 mb-1">Push target preview</p>
			<code class="block text-xs text-surface-9 break-all" data-testid="srt-push-preview">{{ preview }}</code>
		</div>
	</section>
</template>
